/**
 * =========================================================
 * Social Media Hub - Premium Statistics Card
 * =========================================================
 *
 * Modern, sleek statistics card with premium visual design,
 * smooth animations, and polished user experience.
 */

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Color gradients for different card types
const colorGradients = {
  primary: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  secondary: "linear-gradient(135deg, #64748b 0%, #94a3b8 100%)",
  info: "linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)",
  success: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
  warning: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
  error: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
  light: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
  dark: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
};

const colorShadows = {
  primary: "0 8px 24px rgba(99, 102, 241, 0.25)",
  secondary: "0 8px 24px rgba(100, 116, 139, 0.25)",
  info: "0 8px 24px rgba(14, 165, 233, 0.25)",
  success: "0 8px 24px rgba(16, 185, 129, 0.25)",
  warning: "0 8px 24px rgba(245, 158, 11, 0.25)",
  error: "0 8px 24px rgba(239, 68, 68, 0.25)",
  light: "0 8px 24px rgba(0, 0, 0, 0.08)",
  dark: "0 8px 24px rgba(30, 41, 59, 0.25)",
};

function ComplexStatisticsCard({ color, title, count, percentage, icon }) {
  return (
    <Card
      sx={{
        position: "relative",
        overflow: "visible",
        background: "white",
        borderRadius: "20px",
        border: "1px solid rgba(0, 0, 0, 0.04)",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.08)",
        },
        "@media (hover: none)": {
          "&:hover": {
            transform: "none",
          },
          "&:active": {
            transform: "scale(0.98)",
          },
        },
      }}
    >
      {/* Decorative gradient background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "40%",
          height: "100%",
          background: colorGradients[color] || colorGradients.info,
          opacity: 0.05,
          borderRadius: "0 20px 20px 0",
        }}
      />

      <MDBox p={2.5} position="relative">
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
          {/* Icon Container */}
          <Box
            sx={{
              width: { xs: 52, sm: 60 },
              height: { xs: 52, sm: 60 },
              borderRadius: "16px",
              background: colorGradients[color] || colorGradients.info,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: colorShadows[color] || colorShadows.info,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.05) rotate(5deg)",
              },
            }}
          >
            <Icon
              sx={{
                color: color === "light" ? "#1e293b" : "white",
                fontSize: { xs: "1.5rem", sm: "1.75rem" },
              }}
            >
              {icon}
            </Icon>
          </Box>

          {/* Stats */}
          <MDBox textAlign="right" sx={{ flex: 1, ml: 2 }}>
            <MDTypography
              variant="body2"
              color="text"
              sx={{
                fontSize: "0.8rem",
                fontWeight: 500,
                opacity: 0.7,
                mb: 0.5,
              }}
            >
              {title}
            </MDTypography>
            <MDTypography
              variant="h3"
              sx={{
                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
              }}
            >
              {count}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Bottom Section */}
        <MDBox
          mt={2}
          pt={2}
          sx={{
            borderTop: "1px solid rgba(0, 0, 0, 0.06)",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            {percentage.amount && (
              <Box
                sx={{
                  px: 1,
                  py: 0.3,
                  borderRadius: "6px",
                  backgroundColor: `${
                    percentage.color === "success"
                      ? "#10b981"
                      : percentage.color === "error"
                      ? "#ef4444"
                      : percentage.color === "warning"
                      ? "#f59e0b"
                      : "#6366f1"
                  }15`,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Icon
                  sx={{
                    fontSize: "0.9rem",
                    color:
                      percentage.color === "success"
                        ? "#10b981"
                        : percentage.color === "error"
                        ? "#ef4444"
                        : percentage.color === "warning"
                        ? "#f59e0b"
                        : "#6366f1",
                  }}
                >
                  {percentage.color === "success"
                    ? "trending_up"
                    : percentage.color === "error"
                    ? "trending_down"
                    : "trending_flat"}
                </Icon>
                <MDTypography
                  variant="caption"
                  fontWeight="600"
                  sx={{
                    color:
                      percentage.color === "success"
                        ? "#10b981"
                        : percentage.color === "error"
                        ? "#ef4444"
                        : percentage.color === "warning"
                        ? "#f59e0b"
                        : "#6366f1",
                    fontSize: "0.75rem",
                  }}
                >
                  {percentage.amount}
                </MDTypography>
              </Box>
            )}
            <MDTypography
              variant="caption"
              color="text"
              sx={{
                fontSize: "0.75rem",
                opacity: 0.7,
              }}
            >
              {percentage.label}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ComplexStatisticsCard
ComplexStatisticsCard.defaultProps = {
  color: "info",
  percentage: {
    color: "success",
    text: "",
    label: "",
  },
};

// Typechecking props for the ComplexStatisticsCard
ComplexStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  icon: PropTypes.node.isRequired,
};

export default ComplexStatisticsCard;
